import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  observabilityGovernanceApi,
  type AlertRecord,
  type CapacityRecord,
  type MetricRecord,
  type ModuleHealth,
  type ObservabilityGovernanceDashboard,
  type ObservabilityHealth,
  type SLORecord,
  type StructuredLog,
  type TraceRecord,
} from '../../api/observability-governance';

type Tab = 'overview' | 'health' | 'metrics' | 'logs' | 'traces' | 'alerts' | 'slos' | 'capacity';
type Theme = 'light' | 'dark';

function statusColor(status: string, dark: boolean) {
  if (status === 'healthy' || status === 'ok' || status === 'normal') return dark ? 'text-emerald-400' : 'text-emerald-600';
  if (status === 'degraded' || status === 'warning' || status === 'firing') return dark ? 'text-amber-400' : 'text-amber-600';
  if (status === 'unhealthy' || status === 'critical') return dark ? 'text-red-400' : 'text-red-600';
  return dark ? 'text-slate-400' : 'text-slate-500';
}

export function ObservabilityGovernancePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<ObservabilityGovernanceDashboard | null>(null);
  const [health, setHealth] = useState<ObservabilityHealth | null>(null);
  const [modules, setModules] = useState<ModuleHealth[]>([]);
  const [metrics, setMetrics] = useState<MetricRecord[]>([]);
  const [logs, setLogs] = useState<StructuredLog[]>([]);
  const [traces, setTraces] = useState<TraceRecord[]>([]);
  const [alerts, setAlerts] = useState<AlertRecord[]>([]);
  const [slos, setSlos] = useState<SLORecord[]>([]);
  const [capacity, setCapacity] = useState<CapacityRecord[]>([]);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, hlth, mods, mets, logList, trcs, alrts, sloList, cap] = await Promise.all([
        observabilityGovernanceApi.dashboard(),
        observabilityGovernanceApi.health(),
        observabilityGovernanceApi.listModules(),
        observabilityGovernanceApi.listMetrics(),
        observabilityGovernanceApi.listLogs(),
        observabilityGovernanceApi.listTraces(),
        observabilityGovernanceApi.listAlerts(),
        observabilityGovernanceApi.listSLOs(),
        observabilityGovernanceApi.listCapacity(),
      ]);
      setDashboard(dash);
      setHealth(hlth);
      setModules(mods);
      setMetrics(mets);
      setLogs(logList);
      setTraces(trcs);
      setAlerts(alrts);
      setSlos(sloList);
      setCapacity(cap);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load observability platform');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const resolveAlert = async (id: string) => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await observabilityGovernanceApi.resolveAlert(id);
      setActionMsg(result.resolved ? `Alert ${id} resolved` : `Alert ${id} not found`);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Resolve failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'health', label: 'Health Monitor' },
    { id: 'metrics', label: 'Metrics Explorer' },
    { id: 'logs', label: 'Log Explorer' },
    { id: 'traces', label: 'Trace Explorer' },
    { id: 'alerts', label: 'Alert Center' },
    { id: 'slos', label: 'SLO Dashboard' },
    { id: 'capacity', label: 'Capacity Dashboard' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-500">DEV-000085</p>
          <h1 className="text-2xl font-semibold">Enterprise Observability & Performance Platform</h1>
          <p className={`text-sm ${muted}`}>Unified logging, metrics, tracing, health monitoring, SLO management & operational analytics</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/commercial-governance" className="text-sky-500 hover:underline">Commercial Platform</Link>
            <span className={muted}>·</span>
            <Link to="/platform/ai-operations" className="text-sky-500 hover:underline">AI Platform</Link>
            <span className={muted}>·</span>
            <Link to="/observability" className="text-sky-500 hover:underline">Operations Center</Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-2 text-sm ${card}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
          <button type="button" onClick={refresh} disabled={busy} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">Refresh</button>
        </div>
      </div>

      {error && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-red-900 bg-red-950 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>{error}</div>}
      {actionMsg && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-emerald-900 bg-emerald-950 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>{actionMsg}</div>}

      <div className="flex flex-wrap gap-2 border-b pb-2" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-600 text-white' : dark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && health && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Platform Health Score</p><p className="mt-1 text-3xl font-semibold text-sky-500">{health.platform_health_score.toFixed(1)}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Availability</p><p className="mt-1 text-3xl font-semibold">{health.availability_pct.toFixed(2)}%</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>SLO Compliance</p><p className="mt-1 text-3xl font-semibold">{health.slo_compliance_pct.toFixed(1)}%</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Active Alerts</p><p className="mt-1 text-3xl font-semibold text-amber-500">{health.active_alerts}</p></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>MTTD</p><p className="mt-1 text-2xl font-semibold">{health.mttd_mins} min</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>MTTR</p><p className="mt-1 text-2xl font-semibold">{health.mttr_mins} min</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Healthy Modules</p><p className="mt-1 text-2xl font-semibold">{health.healthy_modules}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Capacity Utilization</p><p className="mt-1 text-2xl font-semibold">{health.capacity_utilization_pct.toFixed(1)}%</p></div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h2 className="font-medium">Extension Points</h2>
            <ul className={`mt-2 space-y-1 text-sm ${muted}`}>
              {dashboard.extension_points.map((ep) => (
                <li key={ep.dev_id}><span className="font-mono text-sky-500">{ep.dev_id}</span> — {ep.name}: {ep.description}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'health' && (
        <div className={`overflow-x-auto rounded-xl border ${card}`}>
          <table className="min-w-full text-sm">
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 text-left">Module</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2 text-right">Latency</th><th className="px-4 py-2 text-right">Error Rate</th><th className="px-4 py-2 text-right">Availability</th></tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m.id} className="border-t border-slate-800/20">
                  <td className="px-4 py-2 font-medium">{m.display_name}</td>
                  <td className={`px-4 py-2 capitalize ${statusColor(m.status, dark)}`}>{m.status}</td>
                  <td className={`px-4 py-2 text-right ${muted}`}>{m.latency_ms.toFixed(0)} ms</td>
                  <td className={`px-4 py-2 text-right ${muted}`}>{m.error_rate_pct.toFixed(2)}%</td>
                  <td className="px-4 py-2 text-right">{m.availability_pct.toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'metrics' && (
        <div className={`overflow-x-auto rounded-xl border ${card}`}>
          <table className="min-w-full text-sm">
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-left">Kind</th><th className="px-4 py-2 text-left">Service</th><th className="px-4 py-2 text-right">Value</th><th className="px-4 py-2 text-right">P95</th></tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <tr key={m.id} className="border-t border-slate-800/20">
                  <td className="px-4 py-2 font-mono text-xs">{m.name}</td>
                  <td className={`px-4 py-2 ${muted}`}>{m.kind}</td>
                  <td className={`px-4 py-2 ${muted}`}>{m.service}</td>
                  <td className="px-4 py-2 text-right font-semibold">{m.value.toLocaleString()}</td>
                  <td className={`px-4 py-2 text-right ${muted}`}>{m.p95_ms ? `${m.p95_ms} ms` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'logs' && (
        <div className={`overflow-x-auto rounded-xl border ${card}`}>
          <table className="min-w-full text-sm">
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 text-left">Level</th><th className="px-4 py-2 text-left">Service</th><th className="px-4 py-2 text-left">Message</th><th className="px-4 py-2 text-left">Trace</th></tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-slate-800/20">
                  <td className={`px-4 py-2 uppercase ${statusColor(l.level === 'error' ? 'critical' : l.level === 'warn' ? 'warning' : 'healthy', dark)}`}>{l.level}</td>
                  <td className={`px-4 py-2 ${muted}`}>{l.service}</td>
                  <td className="px-4 py-2">{l.message}</td>
                  <td className={`px-4 py-2 font-mono text-xs ${muted}`}>{l.trace_id ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'traces' && (
        <div className={`overflow-x-auto rounded-xl border ${card}`}>
          <table className="min-w-full text-sm">
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 text-left">Operation</th><th className="px-4 py-2 text-left">Kind</th><th className="px-4 py-2 text-left">Service</th><th className="px-4 py-2 text-right">Duration</th><th className="px-4 py-2 text-left">Status</th></tr>
            </thead>
            <tbody>
              {traces.map((t) => (
                <tr key={t.id} className="border-t border-slate-800/20">
                  <td className="px-4 py-2">{t.operation}</td>
                  <td className={`px-4 py-2 ${muted}`}>{t.kind}</td>
                  <td className={`px-4 py-2 ${muted}`}>{t.service}</td>
                  <td className="px-4 py-2 text-right">{t.duration_ms} ms</td>
                  <td className={`px-4 py-2 capitalize ${statusColor(t.status === 'ok' ? 'healthy' : 'degraded', dark)}`}>{t.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'alerts' && (
        <div className="space-y-4">
          <div className={`overflow-x-auto rounded-xl border ${card}`}>
            <table className="min-w-full text-sm">
              <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
                <tr><th className="px-4 py-2 text-left">Alert</th><th className="px-4 py-2 text-left">Severity</th><th className="px-4 py-2 text-left">Service</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2 text-left">Action</th></tr>
              </thead>
              <tbody>
                {alerts.map((a) => (
                  <tr key={a.id} className="border-t border-slate-800/20">
                    <td className="px-4 py-2"><p className="font-medium">{a.name}</p><p className={`text-xs ${muted}`}>{a.message}</p></td>
                    <td className={`px-4 py-2 capitalize ${statusColor(a.severity === 'critical' ? 'critical' : a.severity === 'warning' ? 'warning' : 'healthy', dark)}`}>{a.severity}</td>
                    <td className={`px-4 py-2 ${muted}`}>{a.service}</td>
                    <td className={`px-4 py-2 capitalize ${statusColor(a.status, dark)}`}>{a.status}</td>
                    <td className="px-4 py-2">{a.status === 'firing' && <button type="button" disabled={busy} onClick={() => resolveAlert(a.id)} className="rounded bg-sky-600 px-2 py-1 text-xs text-white disabled:opacity-50">Resolve</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'slos' && (
        <div className={`overflow-x-auto rounded-xl border ${card}`}>
          <table className="min-w-full text-sm">
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 text-left">SLO</th><th className="px-4 py-2 text-left">Service</th><th className="px-4 py-2 text-right">Target</th><th className="px-4 py-2 text-right">Current</th><th className="px-4 py-2 text-right">Error Budget</th><th className="px-4 py-2 text-left">Compliant</th></tr>
            </thead>
            <tbody>
              {slos.map((s) => (
                <tr key={s.id} className="border-t border-slate-800/20">
                  <td className="px-4 py-2 font-medium">{s.name}</td>
                  <td className={`px-4 py-2 ${muted}`}>{s.service}</td>
                  <td className={`px-4 py-2 text-right ${muted}`}>{s.target_pct}%</td>
                  <td className="px-4 py-2 text-right">{s.current_pct}%</td>
                  <td className={`px-4 py-2 text-right ${muted}`}>{s.error_budget_pct}%</td>
                  <td className={`px-4 py-2 ${s.compliant ? 'text-emerald-500' : 'text-red-500'}`}>{s.compliant ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'capacity' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {capacity.map((c) => (
            <div key={c.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase text-sky-500">{c.module}</p>
                  <h3 className="font-medium">{c.resource.replace(/_/g, ' ')}</h3>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs capitalize ${statusColor(c.status, dark)}`}>{c.status}</span>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <p>Used: <span className="font-semibold">{c.used_pct}%</span></p>
                <p className={muted}>30d forecast: {c.forecast_pct_30d}%</p>
                <p className={muted}>Threshold: {c.threshold_pct}%</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
